import { KeyboardEvent, MouseEvent, useCallback, useMemo } from 'react';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import { ThemeProvider } from '@mui/material/styles';
import {
  DEAL_STAGE_KEY,
  ENGAGEMENT_MANAGER_KEY,
  NOTES_KEY,
  OTHER_CONSULTANT_KEY,
  STATUS_KEY,
} from 'common/constants/analyst-dashboards';
import { ENTER_KEY } from 'common/constants/general';
import { CustomScalarSpreadsheet } from 'components/CustomScalarSpreadsheet';
import useWorkbook from 'components/ScalarSpreadsheet/utilities/useWorkbook';
import useWorkboardSheet from 'dashboard409a/hooks/useWorkboardSheet';
import lightTheme from 'theme';
import { markWorkboardSortHeaderForFocus } from './WorkboardSortHeader';
import { WorkboardSpreadsheetProps } from './WorkboardSpreadsheet.props';

const SKELETON_ROW_COUNT = 12;

const INTERACTIVE_COLUMN_IDS = new Set([
  STATUS_KEY,
  NOTES_KEY,
  OTHER_CONSULTANT_KEY,
  ENGAGEMENT_MANAGER_KEY,
  DEAL_STAGE_KEY,
]);

const WorkboardSkeleton = ({ columnCount }: { columnCount: number }) => (
  <ThemeProvider theme={lightTheme}>
    <Box sx={{ backgroundColor: '#fff', flex: 1, minHeight: 0, overflow: 'hidden', p: 1 }}>
      <Skeleton variant="rectangular" height={36} sx={{ mb: 0.5, borderRadius: 0.5 }} />
      {Array.from({ length: SKELETON_ROW_COUNT }).map((_, i) => (
        <Box key={i} sx={{ display: 'flex', gap: 0.5, mb: 0.5 }}>
          {Array.from({ length: Math.max(columnCount, 1) }).map((__, j) => (
            <Skeleton
              key={j}
              variant="rectangular"
              height={34}
              sx={{ flex: 1, borderRadius: 0.5, opacity: 1 - i * 0.06 }}
            />
          ))}
        </Box>
      ))}
    </Box>
  </ThemeProvider>
);

const WorkboardSpreadsheet = ({
  valuations,
  visibleColumnIds,
  isLoading = false,
  columnWidths,
  statusOptions,
  statusOptionsLoading,
  isNoteLoading = false,
  dealStageOptions,
  sortCriteria,
  dealGroups,
  collapsedDealIds,
  onToggleCollapsedDeal,
  onRequestSort,
  onColumnReorder,
  onColumnResize,
  onRowClick,
  onStatusUpdate,
  onNoteSave,
  onEngagementManagerUpdate,
  onOtherConsultantsUpdate,
  onDealStageChange,
}: WorkboardSpreadsheetProps) => {
  const columnWidthsForSpreadsheet = useMemo(() => {
    if (!columnWidths || Object.keys(columnWidths).length === 0) {
      return undefined;
    }
    // Keys must match columnTypes (column ids) so resize/reorder lookup works.
    return visibleColumnIds.reduce<{ column: string; width: number }[]>((acc, id) => {
      const width = columnWidths[id];
      if (width !== undefined) {
        acc.push({ column: id, width });
      }
      return acc;
    }, []);
  }, [columnWidths, visibleColumnIds]);

  const valuationById = useMemo(
    () => new Map(valuations.map(valuation => [valuation.record_id, valuation])),
    [valuations]
  );

  const handleSheetClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const target = event.target as HTMLElement | null;
      const cell = target?.closest('td') as HTMLTableCellElement | null;
      if (!cell) {
        return;
      }
      if (cell.classList.contains('table-header') || cell.classList.contains('legend')) {
        return;
      }
      if (cell.classList.contains('workboard-deal-group-cell')) {
        return;
      }

      const columnRef = cell.getAttribute('data-column-ref') ?? '';
      if (INTERACTIVE_COLUMN_IDS.has(columnRef) || cell.classList.contains('workboard-interactive-cell')) {
        return;
      }

      const recordId = cell.getAttribute('data-alias');
      if (!recordId) {
        return;
      }

      const valuation = valuationById.get(recordId);
      if (!valuation) {
        return;
      }

      onRowClick(valuation, event);
    },
    [onRowClick, valuationById]
  );

  // Datasheet keeps focus on `.data-grid-container` and eats Tab/Enter for cell nav,
  // so sort headers rarely receive key events. Activate from the selected header cell.
  const handleSortKeyDownCapture = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key !== ENTER_KEY && event.key !== ' ') {
        return;
      }
      if (!onRequestSort) {
        return;
      }

      const target = event.target as HTMLElement | null;
      if (!target) {
        return;
      }

      const grid = target.closest('.data-grid-container');
      if (!grid) {
        return;
      }

      let sortButton = target.closest('[data-workboard-sort-header="true"]') as HTMLElement | null;
      let headerCell = sortButton?.closest('td') as HTMLTableCellElement | null;

      if (!sortButton) {
        headerCell = grid.querySelector('td.cell.selected.table-header') as HTMLTableCellElement | null;
        sortButton = headerCell?.querySelector('[data-workboard-sort-header="true"]') as HTMLElement | null;
      }

      if (!sortButton || !headerCell) {
        return;
      }

      const columnId = headerCell.getAttribute('data-column-ref');
      if (!columnId) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.nativeEvent.stopImmediatePropagation();

      const title = sortButton.getAttribute('data-workboard-sort-title');
      if (title) {
        markWorkboardSortHeaderForFocus(title);
      }

      onRequestSort(event as unknown as MouseEvent<unknown>, columnId, event.shiftKey);
    },
    [onRequestSort]
  );

  const { spreadsheet } = useWorkboardSheet({
    valuations,
    visibleColumnIds,
    statusOptions,
    statusOptionsLoading,
    isNoteLoading,
    dealStageOptions,
    sortCriteria,
    dealGroups,
    collapsedDealIds,
    onToggleCollapsedDeal,
    onRequestSort,
    onStatusUpdate,
    onNoteSave,
    onEngagementManagerUpdate,
    onOtherConsultantsUpdate,
    onDealStageChange,
  });

  const spreadsheets = useMemo(() => [spreadsheet], [spreadsheet]);

  const { cells, data, onChange, workbook } = useWorkbook(spreadsheets);

  if (isLoading || !data || !cells || !data.workboard) {
    return <WorkboardSkeleton columnCount={visibleColumnIds.length} />;
  }

  const sheet = data.workboard;

  return (
    <ThemeProvider theme={lightTheme}>
      <Box
        onClick={handleSheetClick}
        onKeyDownCapture={handleSortKeyDownCapture}
        sx={{
          backgroundColor: '#fff',
          flex: 1,
          minHeight: 0,
          '& .data-grid': {
            width: '100%',
            tableLayout: 'fixed',
          },
          '& .data-grid td.workboard-row-click-cell': {
            cursor: 'pointer',
          },
          // Lift default 175px max-width on notes only — do not use !important so
          // saved/resized inline column widths still win.
          '& .data-grid td.workboard-notes-cell': {
            maxWidth: 'none',
            padding: 0,
            textAlign: 'left',
            overflow: 'hidden',
          },
          '& .data-grid td.workboard-deal-group-cell': {
            backgroundColor: 'rgba(0,0,0,0.025)',
            borderBottom: '1px solid rgba(0,0,0,0.07)',
            verticalAlign: 'middle',
          },
          '& .data-grid td.workboard-empty-message-cell': {
            color: 'text.secondary',
            fontStyle: 'italic',
            textAlign: 'left',
          },
          '& #spreadsheet-table__title-columns': {
            display: 'none',
          },
          '& #spreadsheet-table__content-columns': {
            maxWidth: '100% !important',
            width: '100%',
          },
        }}>
        <CustomScalarSpreadsheet
          isLoading={false}
          data={sheet.data ?? []}
          onChange={onChange}
          sheet={sheet as any}
          columns={sheet.columns}
          rowConfig={sheet.rowConfig}
          cells={cells.workboard}
          workbook={workbook}
          showTitlesColumn={false}
          showTotalColumn={false}
          displayRowIndicator
          displayColumnIndicator
          alwaysDisplayLegend={false}
          columnWidths={columnWidthsForSpreadsheet}
          columnTypes={visibleColumnIds}
          onColumnReorder={onColumnReorder}
          onColumnResize={onColumnResize}
          isReadOnly
          disableVerticalScroll
        />
      </Box>
    </ThemeProvider>
  );
};

export default WorkboardSpreadsheet;
